import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';

// Initialize dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

/**
 * dateTime.js
 * ===========
 * Centralized date/time formatting utilities using dayjs.
 * - All backend timestamps are in UTC (ISO 8601).
 * - For tenant users: converted to tenant.timezone.
 * - For platform users: converted to browser local timezone.
 */

let currentTenantTimezone = undefined;
let currentUserInfo = null;

try {
    const tz = localStorage.getItem('tenant_timezone');
    if (tz) currentTenantTimezone = tz;
    const u = localStorage.getItem('timezone_user');
    if (u) currentUserInfo = JSON.parse(u);
} catch (e) {
    console.debug("localStorage access error for timezone");
}

export const setTenantTimezone = (tz, user = null) => {
    currentTenantTimezone = tz || undefined;
    currentUserInfo = user;
    try {
        if (tz) {
            localStorage.setItem('tenant_timezone', tz);
        } else {
            localStorage.removeItem('tenant_timezone');
        }

        if (user) {
            localStorage.setItem('timezone_user', JSON.stringify(user));
        } else {
            localStorage.removeItem('timezone_user');
        }
    } catch (e) { }
};

/**
 * Returns the effective timezone: Tenant-configured OR Browser local.
 */
export const getActiveTimezone = () => {
    return currentTenantTimezone || dayjs.tz.guess();
};

/**
 * Enhanced debug logger to verify timezone shifts visually.
 * - Only runs in development mode.
 */
const triggerTimezoneDebug = (original, converted, activeTimeZone, label = 'Format') => {
    // Only log in development mode
    if (process.env.NODE_ENV !== 'development') return;

    // console.group(`%c[Timezone Debug: ${label}]`, 'color: #8b5cf6; font-weight: bold;');
    // console.log('Active Timezone:', activeTimeZone);
    // console.log('Input (UTC):', original);
    // console.log('Output (Converted):', converted);
    // console.groupEnd();
};

/**
 * Core utility to parse a value and convert it to the active timezone.
 */
const getTzAgnosticDayjs = (dateVal, activeTimeZone) => {
    if (!dateVal) return null;

    // Always parse as UTC first to prevent local browser shift on load
    const d = dayjs.utc(dateVal);
    if (!d.isValid()) return null;

    // Shift to the target timezone
    return d.tz(activeTimeZone);
};

/**
 * Format a UTC date value to full date + 24h time.
 */
export const formatToLocal24h = (dateVal, timeZone = undefined) => {
    if (!dateVal) return 'N/A';
    try {
        const activeTimeZone = timeZone || getActiveTimezone();
        const d = getTzAgnosticDayjs(dateVal, activeTimeZone);

        if (!d) return 'Invalid Date';

        const result = d.format('DD/MM/YYYY, HH:mm:ss');
        triggerTimezoneDebug(dateVal, result, activeTimeZone, 'FullTime');
        return result;
    } catch (err) {
        console.error('formatToLocal24h error:', err);
        return String(dateVal);
    }
};

/**
 * Format a UTC date value to time only (HH:mm:ss).
 */
export const formatToLocalTime24h = (dateVal, timeZone = undefined) => {
    if (!dateVal) return 'N/A';
    try {
        const activeTimeZone = timeZone || getActiveTimezone();
        const d = getTzAgnosticDayjs(dateVal, activeTimeZone);

        if (!d) return 'Invalid Date';

        return d.format('HH:mm:ss');
    } catch (err) {
        console.error('formatToLocalTime24h error:', err);
        return String(dateVal);
    }
};

/**
 * Format a UTC date string to local date + time + timezone abbreviation.
 */
export const formatWithTimezone = (dateVal, timeZone = undefined) => {
    if (!dateVal) return '—';
    try {
        const activeTimeZone = timeZone || getActiveTimezone();
        const d = getTzAgnosticDayjs(dateVal, activeTimeZone);

        if (!d) return 'Invalid Date';

        // dayjs doesn't have a built-in "IST/PST" format token for full names in core, 
        // we append it manually or use z/zz with certain plugins. 
        // For simplicity and accuracy with Intl, we combine.
        const datePart = d.format('DD/MM/YYYY, HH:mm:ss');
        const tzPart = d.format('z'); // This might need timezone plugin enabled

        const result = tzPart ? `${datePart} ${tzPart}` : datePart;
        triggerTimezoneDebug(dateVal, result, activeTimeZone, 'WithTZ');
        return result;
    } catch (err) {
        return String(dateVal);
    }
};

/**
 * Formats a UTC date value as a relative time string (e.g. "5m ago", "Just now").
 * CRITICAL: Enforces same-timezone comparison to fix "5h ago" bugs.
 */
export const formatRelativeTime = (dateVal) => {
    if (!dateVal) return ' — ';

    try {
        const activeTimeZone = getActiveTimezone();

        // 1. Get current time in active timezone
        const now = dayjs().tz(activeTimeZone);

        // 2. Parse input and convert to same active timezone
        const then = dayjs.utc(dateVal).tz(activeTimeZone);

        if (!then.isValid()) {
            return ' — ';
        }

        // 3. Safety Check: Future Time (Clock Mismatch)
        // If 'then' is after 'now', return "Just now"
        if (then.isAfter(now)) {
            return 'Just now';
        }

        // 4. Custom small interval handling (dayjs.fromNow is sometimes a bit rounded)
        const diffInSeconds = now.diff(then, 'second');
        if (diffInSeconds < 60) {
            return 'Just now';
        }

        // 5. Use relativeTime plugin for standard ranges
        return then.from(now);

    } catch (err) {
        console.error('formatRelativeTime error:', err);
        return ' — ';
    }
};

/**
 * Format a UTC date value to a readable local format (e.g., 09 Apr 2026, 21:20).
 * This is the standard for absolute time display in the UI.
 * Rules:
 * - null/undefined/invalid -> return "—"
 * - 24-hour format (HH:mm)
 */
export const formatDateTime = (dateVal, timeZone = undefined) => {
    if (!dateVal) return '—';
    try {
        const activeTimeZone = timeZone || getActiveTimezone();
        const d = getTzAgnosticDayjs(dateVal, activeTimeZone);

        if (!d) return '—';

        // Standard Format: DD MMM YYYY, HH:mm
        const result = d.format('DD MMM YYYY, HH:mm');
        triggerTimezoneDebug(dateVal, result, activeTimeZone, 'AbsoluteDateTime');
        return result;
    } catch (err) {
        console.error('formatDateTime error:', err);
        return '—';
    }
};
